import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { channels, RealtimeEventName } from '@outfalak/shared'
import { api, ApiError } from '../lib/api.js'
import { useAuth } from './useAuth.js'
import { useRealtimeEvent } from './useRealtime.js'

/**
 * The topbar bell.
 *
 * `/v1/alerts` derives its list from orders, refunds, top-ups and the audit
 * trail, so there is nothing to subscribe to directly — but every event that
 * *changes* that list already comes over the socket for other screens. This
 * hook listens to those and invalidates, which is the same pattern the orders
 * table and the dashboard KPIs use, and it means the badge moves the moment a
 * payment lands rather than on the next poll.
 *
 * The interval underneath it is a floor, not the mechanism. A store can be
 * uninstalled or a Shopify usage cap hit without any socket traffic reaching
 * this browser at all, and those are exactly the entries worth not missing.
 */

/** One row in the menu. Mirrors `Alert` in the API's `modules/alerts/feed.ts`. */
export interface Alert {
  id: string
  /** Null for a condition the platform records no moment for. */
  at: string | null
  ago: string
  icon: string
  tone: string
  title: string
  meta: string
  to: string | null
  /** A standing condition — a low wallet, an unapproved charge — not a moment. */
  pinned: boolean
  unread: boolean
}

export interface AlertFeed {
  items: Alert[]
  unread: number
  readAt: string | null
}

/** Slow on purpose: the socket carries everything that is urgent. */
const POLL_MS = 180_000

export interface AlertsValue {
  feed: AlertFeed | undefined
  loading: boolean
  /**
   * What went wrong, in the API's own words where it has any.
   *
   * Not a boolean: an operator with no store selected gets a 403 saying exactly
   * that, and rendering it as "could not reach the server" sends them to check
   * their connection over a sentence that already told them what to do.
   */
  error: string | null
  /** Null until a store is chosen — there is no feed without one. */
  shopId: string | null
  markRead: () => void
}

export function useAlerts(): AlertsValue {
  const { user, shop } = useAuth()
  const queryClient = useQueryClient()
  const shopId = shop?.id ?? null
  const key = ['alerts', shopId]

  const { data, isPending, error } = useQuery({
    queryKey: key,
    queryFn: () => api<AlertFeed>('/v1/alerts'),
    enabled: Boolean(shopId),
    refetchInterval: POLL_MS,
  })

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['alerts', shopId] })
  }, [queryClient, shopId])

  const ordersChannel = shopId ? channels.shopOrders(shopId) : null
  const paymentsChannel = shopId ? channels.shopPayments(shopId) : null
  // Account-wide, not per store: a top-up made from one storefront's screen
  // changes the balance every other storefront on the account spends from.
  const walletChannel = user?.merchantId ? channels.merchantWallet(user.merchantId) : null

  useRealtimeEvent(ordersChannel, RealtimeEventName.OrderCreated, invalidate)
  useRealtimeEvent(ordersChannel, RealtimeEventName.OrderUpdated, invalidate)
  useRealtimeEvent(paymentsChannel, RealtimeEventName.PaymentSucceeded, invalidate)
  useRealtimeEvent(paymentsChannel, RealtimeEventName.PaymentFailed, invalidate)
  useRealtimeEvent(paymentsChannel, RealtimeEventName.RefundProcessed, invalidate)
  useRealtimeEvent(walletChannel, RealtimeEventName.WalletLow, invalidate)
  useRealtimeEvent(walletChannel, RealtimeEventName.WalletChanged, invalidate)

  const { mutate } = useMutation({
    mutationFn: () => api<{ readAt: string; unread: number }>('/v1/alerts/read', { method: 'POST' }),
    /*
     * Optimistic, and deliberately so. Clearing the badge is the one thing the
     * merchant asked for by opening the menu; making them watch a round trip
     * for it — and watching it come back if the network is slow — is worse than
     * being wrong for the half-second before the refetch agrees.
     */
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AlertFeed>(key)

      if (previous) {
        queryClient.setQueryData<AlertFeed>(key, {
          ...previous,
          unread: 0,
          items: previous.items.map((item) => ({ ...item, unread: false })),
        })
      }

      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: invalidate,
  })

  const markRead = useCallback(() => {
    if (shopId && (data?.unread ?? 0) > 0) mutate()
  }, [shopId, data?.unread, mutate])

  return {
    feed: data,
    loading: Boolean(shopId) && isPending,
    error: error ? explain(error) : null,
    shopId,
    markRead,
  }
}

/**
 * A 4xx is the server having an opinion and wording it for the merchant; a 5xx
 * or a dropped connection is not, and passing its message through would put an
 * internal sentence in a menu.
 */
function explain(error: unknown): string {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return error.message
  }
  return 'Could not reach the server. This list will fill in once it answers.'
}
